import type { CoverAiPicture } from '../../../../redux/api/fymAPI.types';

/** The pictures of one run, in the order of the answer. */
export interface CoverAiRun {
  runUuid: string;
  pictures: CoverAiPicture[];
  isWorking: boolean;
}

/** True while a picture is still being made. Pure. */
export function isCoverPictureWorking(picture: Pick<CoverAiPicture, 'status'>): boolean {
  return picture.status === 'queued' || picture.status === 'working';
}

/**
 * The AI pictures of the song as runs, the newest run first (the answer lists the newest picture
 * first). Pure.
 */
export function coverAiRuns(pictures: readonly CoverAiPicture[]): CoverAiRun[] {
  const runs = new Map<string, CoverAiPicture[]>();
  for (const picture of pictures) {
    const run = runs.get(picture.runUuid);
    if (run === undefined) {
      runs.set(picture.runUuid, [picture]);
    } else {
      run.push(picture);
    }
  }
  return [...runs.entries()].map(([runUuid, items]) => ({
    runUuid,
    pictures: items,
    isWorking: items.some(isCoverPictureWorking),
  }));
}

/** The ready pictures of the runs before the newest one: the "Earlier" strip. Pure. */
export function earlierCoverPictures(runs: readonly CoverAiRun[]): CoverAiPicture[] {
  return runs
    .slice(1)
    .flatMap((run) => run.pictures)
    .filter((picture) => picture.status === 'ready' && picture.url !== null);
}

/** True when every picture of the run failed with the same refusal: the user must change the request. Pure. */
export function isRunRefused(run: CoverAiRun): boolean {
  return run.pictures.length > 0 && run.pictures.every((picture) => picture.failCode === 'IMAGE_REFUSED');
}

/** True when every picture of the run failed. Pure. */
export function isRunFailed(run: CoverAiRun): boolean {
  return run.pictures.length > 0 && run.pictures.every((picture) => picture.status === 'failed');
}
